/**
 * Boli Live - Main App Component
 *
 * Premium auction platform with smooth animations.
 */

import { Routes, Route, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useAuthStore } from './lib/stores/auth-store';

// Pages
import HomePage from './pages/HomePage';
import CategoryPage from './pages/CategoryPage';
import AuctionPage from './pages/AuctionPage';
import ProductPage from './pages/ProductPage';
import StorePage from './pages/StorePage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import ProfilePage from './pages/ProfilePage';
import SellerDashboard from './pages/SellerDashboard';
import GoLivePage from './pages/GoLivePage';
import OrdersPage from './pages/OrdersPage';
import SavedPage from './pages/SavedPage';
import InboxPage from './pages/InboxPage';
import NotFoundPage from './pages/NotFoundPage';
// Seller Pages
import ProductManagementPage from './pages/ProductManagementPage';
import CreateProductPage from './pages/CreateProductPage';
import EditProductPage from './pages/EditProductPage';
import StoreSettingsPage from './pages/StoreSettingsPage';
import WalletPage from './pages/WalletPage';

// Admin Pages
import AdminDashboard from './pages/AdminDashboard';
import UserManagementPage from './pages/UserManagementPage';
import AuditLogsPage from './pages/AuditLogsPage';

// KYC & Messaging Pages
import KYCVerificationPage from './pages/KYCVerificationPage';
import MessagesPage from './pages/MessagesPage';
import ChatPage from './pages/ChatPage';

// Components
import { BottomNav } from './components/BottomNav';
import { TopNav } from './components/TopNav';
import { CartDrawer } from './components/CartDrawer';
import { AuthGuard } from './components/AuthGuard';
import { AdminGuard } from './components/AdminGuard';

function App() {
  const { initialize } = useAuthStore();
  const location = useLocation();

  useEffect(() => {
    initialize();
  }, [initialize]);

  const isFullScreen = location.pathname.startsWith('/auction/');

  return (
    <div className="min-h-screen bg-background text-foreground font-sans">
      {!isFullScreen && <TopNav />}

      <main className={!isFullScreen ? "pb-20" : ""}>
        <AnimatePresence mode="wait">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/category/:category" element={<CategoryPage />} />
            <Route path="/category/:category/:subCategory" element={<CategoryPage />} />
            <Route path="/auction/:id" element={<AuctionPage />} />
            <Route path="/product/:id" element={<ProductPage />} />
            <Route path="/store/:slug" element={<StorePage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/profile" element={<ProfilePage />} />

            {/* Protected Routes */}
            <Route
              path="/wallet"
              element={
                <AuthGuard>
                  <WalletPage />
                </AuthGuard>
              }
            />
            <Route
              path="/checkout"
              element={
                <AuthGuard>
                  <CheckoutPage />
                </AuthGuard>
              }
            />
            <Route
              path="/saved"
              element={
                <AuthGuard>
                  <SavedPage />
                </AuthGuard>
              }
            />
            <Route
              path="/inbox"
              element={
                <AuthGuard>
                  <InboxPage />
                </AuthGuard>
              }
            />
            <Route
              path="/seller"
              element={
                <AuthGuard>
                  <SellerDashboard />
                </AuthGuard>
              }
            />
            <Route
              path="/seller/products"
              element={
                <AuthGuard>
                  <ProductManagementPage />
                </AuthGuard>
              }
            />
            <Route
              path="/seller/products/new"
              element={
                <AuthGuard>
                  <CreateProductPage />
                </AuthGuard>
              }
            />
            <Route
              path="/seller/products/:id/edit"
              element={
                <AuthGuard>
                  <EditProductPage />
                </AuthGuard>
              }
            />
            <Route
              path="/seller/settings"
              element={
                <AuthGuard>
                  <StoreSettingsPage />
                </AuthGuard>
              }
            />
            <Route
              path="/go-live"
              element={
                <AuthGuard>
                  <GoLivePage />
                </AuthGuard>
              }
            />
            <Route
              path="/orders"
              element={
                <AuthGuard>
                  <OrdersPage />
                </AuthGuard>
              }
            />
            <Route
              path="/kyc"
              element={
                <AuthGuard>
                  <KYCVerificationPage />
                </AuthGuard>
              }
            />
            <Route
              path="/messages"
              element={
                <AuthGuard>
                  <MessagesPage />
                </AuthGuard>
              }
            />
            <Route
              path="/messages/:threadId"
              element={
                <AuthGuard>
                  <ChatPage />
                </AuthGuard>
              }
            />

            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <AdminGuard>
                  <AdminDashboard />
                </AdminGuard>
              }
            />
            <Route
              path="/admin/users"
              element={
                <AdminGuard>
                  <UserManagementPage />
                </AdminGuard>
              }
            />
            <Route
              path="/admin/audit-logs"
              element={
                <AdminGuard requiredRole="admin">
                  <AuditLogsPage />
                </AdminGuard>
              }
            />

            {/* 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </AnimatePresence>
      </main>

      {/* Bottom Navigation - Mobile First */}
      {!isFullScreen && <BottomNav />}

      {/* Cart Drawer (slide-over) */}
      <CartDrawer />
    </div>
  );
}

export default App;
